// Voice hooks: Web Speech API STT + TTS
import { useEffect, useRef, useState, useCallback } from 'react';

export function useSpeechRecognition() {
    const recognitionRef = useRef(null);
    const [supported, setSupported] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            setSupported(false);
            return;
        }
        setSupported(true);
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-IN';

        rec.onresult = (evt) => {
            let final = '';
            let interim = '';
            for (let i = evt.resultIndex; i < evt.results.length; i++) {
                const t = evt.results[i][0].transcript;
                if (evt.results[i].isFinal) final += t + ' ';
                else interim += t;
            }
            setTranscript((prev) => (final ? prev + final : prev + interim));
        };
        rec.onerror = (e) => {
            setError(e.error || 'speech-recognition-error');
            setIsListening(false);
        };
        rec.onend = () => setIsListening(false);

        recognitionRef.current = rec;
        return () => {
            try { rec.stop(); } catch (_) { /* ignore */ }
        };
    }, []);

    const start = useCallback(() => {
        setError(null);
        if (!recognitionRef.current) return;
        try {
            recognitionRef.current.start();
            setIsListening(true);
        } catch (_) {
            // already started
        }
    }, []);

    const stop = useCallback(() => {
        if (!recognitionRef.current) return;
        try { recognitionRef.current.stop(); } catch (_) { /* ignore */ }
        setIsListening(false);
    }, []);

    const reset = useCallback(() => setTranscript(''), []);

    return { supported, isListening, transcript, setTranscript, start, stop, reset, error };
}

export function speakText(text) {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.rate = 1.0;
        u.pitch = 1.0;
        u.lang = 'en-IN';
        window.speechSynthesis.speak(u);
    } catch (_) {
        /* ignore */
    }
}

export function stopSpeaking() {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try { window.speechSynthesis.cancel(); } catch (_) { /* ignore */ }
}
